import React from 'react';

import { formatWholeMoney } from '~support-at-home/lib/budgetFormat';

import { ClickableIcon, SectionCard, TabContainer } from '~components';
import Icon, { faExpand, faInfoCircle } from '~components/icon';
import { Column, Row, TableView } from '~components/table-view';
import SectionInfo from '~support-at-home/components/common/SectionInfo';

import { DASHBOARD_TEXT } from './dashboardText';
import { DashboardWidgetStatus, READY_WIDGET } from './dashboardWidgets';
import { DashboardWidgetState } from './DashboardWidgetState';

/** Rows shown in the widget; the rest are behind the expand icon (the full table). */
const WIDGET_ROWS = 5;

export interface ListView {
  /** Unique within the list: 'over' or 'under'. Passed to onViewChange and onShowAll. */
  key: string;
  /** The tab's name, e.g. "Over-allocated". */
  label: string;
  /** The amounts of every row in the view added up, shown under the tab's name. Omit for a view with no total. */
  total?: number;
  columns: Column[];
  /** Every row, ranked: the widget shows the first five. */
  rows: Row[];
  emptyText: string;
}

interface ComponentProps {
  title: string;
  description: React.ReactNode;
  /** The section's explanation behind the info icon. */
  info: React.ReactNode;
  /** One view, or two (Over and Under) as tabs. */
  views: ListView[];
  activeKey: string;
  onViewChange?: (key: string) => void;
  onShowAll: (key: string) => void;
  dataCy: string;
  /** Loading or failed: the state box replaces the tabs, rows and footer, under the title. Default ready. */
  status?: DashboardWidgetStatus;
}

const copy = DASHBOARD_TEXT.list;

/**
 * A Dashboard list (TAV-3463): the first five rows of a ranked list, Over/Under tabs where there are two views, how
 * many are shown, and an expand icon (bottom right) that opens the full table. The rows are all in the page's data
 * already, so nothing here waits on the api.
 */
export const DashboardList: React.FC<ComponentProps> = ({
  title,
  description,
  info,
  views,
  activeKey,
  onViewChange,
  onShowAll,
  dataCy,
  status = READY_WIDGET,
}) => {
  const view = views.find(({ key }) => key === activeKey) ?? views[0];
  const count = view.rows.length;

  const renderRows = ({ rows, columns, emptyText }: ListView, tableId: string) =>
    rows.length > 0 ? (
      <TableView id={tableId} data={rows.slice(0, WIDGET_ROWS)} columns={columns} readonly />
    ) : (
      <div className="no-data" role="status">
        <Icon icon={faInfoCircle} />
        {emptyText}
      </div>
    );

  return (
    <SectionCard
      title={title}
      description={description}
      titleAddon={<SectionInfo id={`${dataCy}-section`} label={title} content={info} />}
      className="dashboard-list"
      dataCy={dataCy}
    >
      {status.phase !== 'ready' && <DashboardWidgetState status={status} title={title} dataCy={dataCy} />}
      {status.phase === 'ready' && views.length > 1 && (
        <TabContainer
          className="dashboard-list-tabs"
          setPageTitle={false}
          activeTab={views.indexOf(view)}
          onTabChange={(index) => onViewChange?.(views[index].key)}
          tabs={views.map((listView) => ({
            id: `${dataCy}-${listView.key}`,
            // Two lines: the view's name, then its total (.dashboard-list-tabs styles the lines apart); just the name
            // when the view has no total
            title:
              listView.total === undefined ? listView.label : `${listView.label}\n${formatWholeMoney(listView.total)}`,
            content: renderRows(listView, `${dataCy}-${listView.key}-table`),
          }))}
        />
      )}
      {status.phase === 'ready' && views.length <= 1 && renderRows(view, `${dataCy}-table`)}
      {status.phase === 'ready' && count > 0 && (
        <div className="dashboard-list-footer" data-cy={`${dataCy}-footer`}>
          <span>{count > WIDGET_ROWS ? copy.showing(WIDGET_ROWS, count) : copy.showingAll(count)}</span>
          <ClickableIcon
            icon={faExpand}
            id={`${dataCy}-expand`}
            onClick={() => onShowAll(view.key)}
            toolTip={copy.showAll}
            ariaLabel={copy.showAllLabel(title)}
          />
        </div>
      )}
    </SectionCard>
  );
};
